import React, { useState, useEffect } from 'react';
import {
    Target,
    CheckCircle,
    Clock,
    Filter,
    Calendar,
    User,
    AlertCircle,
    TrendingUp,
    CircleDashed,
    CircleOff
} from 'lucide-react';
import apiService from '../services/api';
import { formatDateWithDefaultTimezone } from '../utils/timezone';

const LeadsManagement = () => {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        username: '',
        dateFrom: '',
        dateTo: '',
        claimed: '',
        state: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 0
    });
    const [defaultTimezone, setDefaultTimezone] = useState('UTC');

    useEffect(() => {
        fetchData();
    }, [filters, pagination.currentPage, pagination.itemsPerPage]);

    useEffect(() => {
        fetchUsers();
        fetchConfig();
    }, []);

    const fetchUsers = async () => {
        try {
            const usersData = await apiService.getUsers();
            // Sort users by priority for proper display
            const sortedUsers = usersData.sort((a, b) => a.priority - b.priority);
            setUsers(sortedUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const fetchConfig = async () => {
        try {
            const configData = await apiService.getConfig();
            const defaultConfig = configData.find(config => config.username === null);
            if (defaultConfig && defaultConfig.timeZone) {
                setDefaultTimezone(defaultConfig.timeZone);
            }
        } catch (err) {
            console.error('Error fetching config:', err);
        }
    };

    const fetchData = async () => {
        try {
            setError('');

            // Add pagination parameters to filters
            const filtersWithPagination = {
                ...filters,
                page: pagination.currentPage,
                limit: pagination.itemsPerPage
            };

            // Fetch both leads and stats in parallel
            const [leadsResponse, statsData] = await Promise.all([
                apiService.getLeads(filtersWithPagination),
                apiService.getLeadStats(filters)
            ]);

            setLeads(leadsResponse.data || leadsResponse);
            setStats(statsData);

            // Update pagination info if available
            if (leadsResponse.pagination) {
                setPagination(prev => ({
                    ...prev,
                    currentPage: leadsResponse.pagination.currentPage,
                    itemsPerPage: leadsResponse.pagination.itemsPerPage,
                    totalItems: leadsResponse.pagination.totalItems,
                    totalPages: leadsResponse.pagination.totalPages
                }));
            }
        } catch (err) {
            console.error('Error fetching leads:', err);
            setError('Failed to fetch leads data');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        // Reset to page 1 when filters change
        setPagination(prev => ({
            ...prev,
            currentPage: 1
        }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            currentPage: newPage
        }));
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setPagination(prev => ({
            ...prev,
            itemsPerPage: parseInt(newItemsPerPage),
            currentPage: 1 // Reset to page 1 when changing items per page
        }));
    };

    const clearFilters = () => {
        setFilters({
            username: '',
            dateFrom: '',
            dateTo: '',
            claimed: '',
            state: ''
        });
    };

    const formatDate = (date) => {
        return formatDateWithDefaultTimezone(date, defaultTimezone);
    };

    const getLeadStatusIcon = (lead) => {
        switch (lead.state) {
            case 'claimed':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-500" />;
            case 'ignored':
                return <CircleDashed className="w-5 h-5 text-gray-500" />;
            case 'lost':
                return <CircleOff className="w-5 h-5 text-red-500" />;
            default:
                return <Clock className="w-5 h-5 text-yellow-500" />;
        }
    };

    const getLeadStatusText = (lead) => {
        switch (lead.state) {
            case 'claimed':
                return 'Claimed';
            case 'pending':
                return 'Pending';
            case 'ignored':
                return 'Ignored';
            case 'lost':
                return 'Lost';
            default:
                return 'Pending';
        }
    };

    const getLeadStatusColor = (lead) => {
        switch (lead.state) {
            case 'claimed':
                return 'text-green-600';
            case 'pending':
                return 'text-yellow-600';
            case 'ignored':
                return 'text-gray-600';
            case 'lost':
                return 'text-red-600';
            default:
                return 'text-yellow-600';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">Leads Management</h1>
                <button
                    onClick={fetchData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <TrendingUp className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Statistics Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total valid Leads</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.overview.validLeads}</p>
                            </div>
                            <Target className="h-8 w-8 text-blue-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Claimed</p>
                                <p className="text-2xl font-bold text-green-600">{stats.overview.claimedLeads}</p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Lost</p>
                                <p className="text-2xl font-bold text-red-600">{stats.overview.lostLeads}</p>
                            </div>
                            <CircleOff className="h-8 w-8 text-red-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.overview.claimRate ? stats.overview.claimRate : '0'}%</p>
                            </div>
                            <TrendingUp className="h-8 w-8 text-purple-600" />
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Filter className="w-5 h-5 text-gray-500" />
                    <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Username
                        </label>
                        <select
                            value={filters.username}
                            onChange={(e) => handleFilterChange('username', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Users</option>
                            {users.map((user) => (
                                <option key={user.username} value={user.username}>
                                    {user.username} (Priority: {user.priority})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            From Date
                        </label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            To Date
                        </label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                        </label>
                        <select
                            value={filters.state || ''}
                            onChange={(e) => handleFilterChange('state', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All States</option>
                            <option value="claimed">Claimed</option>
                            <option value="pending">Pending</option>
                            <option value="ignored">Ignored</option>
                            <option value="lost">Lost</option>
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* User Statistics */}
            {stats && stats.userStats && stats.userStats.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">User Performance</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Username
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total valid
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Claimed
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Lost
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Success Rate
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.userStats.map((userStat) => (
                                    <tr key={userStat.username} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <User className="w-4 h-4 text-gray-400 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">{userStat.username}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {userStat.valid}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                            {userStat.claimed}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                                            {userStat.lost}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 font-medium">
                                            {userStat.claimRate}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Leads Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Leads ({pagination.totalItems} total)
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Lead ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Username
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Found At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Claimed At
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {leads.map((lead) => (
                                <tr key={lead._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {lead.leadId || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <User className="w-4 h-4 text-gray-400 mr-2" />
                                            <span className="text-sm font-medium text-gray-900">{lead.username}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                            {formatDate(lead.foundAt)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {getLeadStatusIcon(lead)}
                                            <span className={`ml-2 text-sm font-medium ${getLeadStatusColor(lead)}`}>
                                                {getLeadStatusText(lead)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {lead.claimedAt ? formatDate(lead.claimedAt) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {leads.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            No leads found with the current filters.
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700">Show:</label>
                            <select
                                value={pagination.itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-sm text-gray-500">per page</span>
                        </div>
                        <div className="text-sm text-gray-700">
                            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} results
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (pagination.currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                } else {
                                    pageNum = pagination.currentPage - 2 + i;
                                }

                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-3 py-1 border rounded-md text-sm font-medium ${pageNum === pagination.currentPage
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadsManagement;
